import { atomFamily, atomWithStorage } from 'jotai/utils'

import { DEFAULT_AUTO_HIDE_DAYS } from '@/features/dashboard/lib/page-visibility'
import { DEFAULT_COLUMNS_ORDER } from '@/features/dashboard/lib/position-table-config'
import { LOCAL_STORAGE_KEYS } from '@/lib/configs/auth-config'
import { SUPPORTED_EXCHANGE } from '@/lib/configs/exchange-config'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { localeNumberFormat, type NumberFormatPrefs } from '@/lib/number-format'
import {
  clearPendingStorageWrites,
  getCurrentUserId,
  namespacedKey,
  safeStorage,
} from '@/lib/safe-storage'
import { DashboardConifg } from '@/types/dashboard'
import { PublicAccount } from '@/types/generated/common/account'
import { InstrumentKind } from '@/types/generated/common/types'

export { safeStorage } from '@/lib/safe-storage'

// Dashboard Storage
export const exchangeAtom = atomWithStorage<PublicAccount | null>(
  STORAGE_KEYS.dashboard.exchange,
  null,
  safeStorage,
  { getOnInit: true }
)

// Shared shape for "recently selected" ticker pickers (option-ticker-picker, watch-list-picker).
// `type`/`exchange`/`pricePrecision`/`tickSize` are only populated by pickers that need them to
// reconstruct a full instrument selection (e.g. watch-list-picker); option-ticker-picker only
// needs symbol/baseCurrency/kind.
export type RecentTickerEntry = {
  symbol: string
  baseCurrency: string
  kind: InstrumentKind
  type?: 'spot' | 'future' | 'perps' | 'options'
  exchange?: keyof typeof SUPPORTED_EXCHANGE
  pricePrecision?: string
  tickSize?: string
}

export const recentWatchTickersAtom = atomWithStorage<RecentTickerEntry[]>(
  STORAGE_KEYS.dashboard.recentWatchTickers,
  [],
  safeStorage,
  { getOnInit: true }
)

/** The Percept chart toolbar's starred timeframes (seconds; Tick = CHART_TICK_TIMEFRAME): shown
 *  as quick buttons, starred/unstarred from its timeframe menu. Per browser, like TV's own. */
export const perceptFavoriteTimeframesAtom = atomWithStorage<number[]>(
  STORAGE_KEYS.dashboard.perceptFavoriteTimeframes,
  [60, 300, 3600],
  safeStorage,
  { getOnInit: true }
)

export const recentCalendarAreasAtom = atomWithStorage<string[]>(
  STORAGE_KEYS.dashboard.recentCalendarAreas,
  [],
  safeStorage,
  { getOnInit: true }
)

export const recentOptionTickersAtom = atomWithStorage<RecentTickerEntry[]>(
  STORAGE_KEYS.dashboard.recentOptionTickers,
  [],
  safeStorage,
  { getOnInit: true }
)

export type WatchList = {
  list: {
    id: string
    symbol: string
    type: 'spot' | 'future' | 'perps' | 'options'
    exchange: keyof typeof SUPPORTED_EXCHANGE
    /** Account selected when this chart tab was opened. */
    accountId?: string
    pricePrecision?: string
    tickSize?: string
    timeframe?: number // Per-tab timeframe in seconds (default: 3600 = 1 hour)
    expiry?: string // Option expiry timestamp (for options type)
    expirationTsMs?: string // Exact option expiry timestamp from the instrument
    selectedOptionSymbol?: string // Last selected specific option instrument for this tab
  }[]
  // Canonical active tab owner. Prefer this over selectedIndex when preserving focus
  // across reorder/remove/server restore flows.
  focusedTabId?: string | null
  // Legacy mirror for components that still address tabs by array index.
  // useLinkingGroupState normalizes this from focusedTabId.
  selectedIndex: number
}

export const watchListAtom = atomWithStorage<WatchList>(
  STORAGE_KEYS.dashboard.watchList,
  {
    list: [],
    focusedTabId: null,
    selectedIndex: -1,
  },
  safeStorage,
  {
    getOnInit: true,
  }
)

export const DEFAULT_DASHBOARD_COLUMNS: DashboardConifg['columns'] = {
  instrument: true,
  type: true,
  size: true,
  delta: true,
  deltaUSD: true,
  gamma: false,
  gammaUSD: false,
  vega: false,
  vegaUSD: false,
  theta: false,
  thetaUSD: false,
  rho: false,
  rhoUSD: false,
  entry: true,
  entryIv: false,
  mark: true,
  markIv: false,
  pnl: true,
  pnlUSD: false,
  pnlPercent: true,
  netPnl: false,
  realizedPnl: false,
  realizedPnlUSD: false,
  im: false,
  imUSD: false,
  mm: false,
  mmUSD: false,
  liquidationPrice: false,
  dailyUnrealizedPnl: false,
  dailyUnrealizedPnlUsd: false,
  realizedFunding: false,
  expiry: false,
  account: true,
}

const DEFAULT_DASHBOARD_CONFIG: DashboardConifg = {
  columns: DEFAULT_DASHBOARD_COLUMNS,
  columnsOrder: DEFAULT_COLUMNS_ORDER,
  positionFilter: {
    instruments: { disabled: false, value: ['ALL'] },
    expiries: { disabled: false, value: ['ALL'] },
    assets: { disabled: false, value: ['ALL'] },
    accounts: { disabled: false, value: ['ALL'] },
    size: { disabled: false, value: null },
    pnl: { disabled: false, value: null },
    strikeRange: { disabled: false, value: null },
    positionDelta: { disabled: false, value: null },
  },
}

// The real dashboard grid's column/filter config is intentionally shared across every trade
// page — this is the scope every existing caller gets by default (DashboardConfigScopeContext,
// see dashboard-config-scope-context.ts).
export const DASHBOARD_CONFIG_GLOBAL_SCOPE = 'global'

// A standalone view with no real grid widget (Risk Slides, Delta Hedge — see risk-slides-page.tsx
// / hedge-page.tsx) still wants its Positions table's column/settings choices to persist
// locally, but scoped to itself rather than sharing (and overwriting) the main dashboard's
// config, or trying to sync to a backend widget id that doesn't exist. atomFamily keeps
// `dashboardConfigAtom` below as a stable, backward-compatible reference for the (many) existing
// callers that only ever want the global scope.
// Per-scope column defaults. The Delta Hedge page (scope 'hedge', HEDGE_LINKING_PAGE_ID in
// hedge-page.tsx) shows the greeks + PnL its builder used to sum in a "Live metrics" box.
const SCOPE_DEFAULT_COLUMNS: Record<string, Partial<DashboardConifg['columns']>> = {
  hedge: { delta: true, gamma: true, vega: true, pnl: true },
}

export const dashboardConfigAtomFamily = atomFamily((scope: string) =>
  atomWithStorage<DashboardConifg>(
    scope === DASHBOARD_CONFIG_GLOBAL_SCOPE
      ? STORAGE_KEYS.dashboard.config
      : `${STORAGE_KEYS.dashboard.config}-${scope}`,
    SCOPE_DEFAULT_COLUMNS[scope]
      ? {
          ...DEFAULT_DASHBOARD_CONFIG,
          columns: { ...DEFAULT_DASHBOARD_COLUMNS, ...SCOPE_DEFAULT_COLUMNS[scope] },
        }
      : DEFAULT_DASHBOARD_CONFIG,
    safeStorage,
    { getOnInit: true }
  )
)

export const dashboardConfigAtom = dashboardConfigAtomFamily(DASHBOARD_CONFIG_GLOBAL_SCOPE)

export const pageTabsOrderAtom = atomWithStorage<string[]>(
  STORAGE_KEYS.dashboard.pageTabsOrder,
  [],
  safeStorage,
  { getOnInit: true }
)

export const pageLastUsedAtAtom = atomWithStorage<Record<string, number>>(
  STORAGE_KEYS.dashboard.pageLastUsedAt,
  {},
  safeStorage,
  { getOnInit: true }
)

export const pinnedPageIdsAtom = atomWithStorage<string[]>(
  STORAGE_KEYS.dashboard.pinnedPageIds,
  [],
  safeStorage,
  { getOnInit: true }
)

export const hiddenPageIdsAtom = atomWithStorage<string[]>(
  STORAGE_KEYS.dashboard.hiddenPageIds,
  [],
  safeStorage,
  { getOnInit: true }
)

export const pageAutoHideDaysAtom = atomWithStorage<number>(
  STORAGE_KEYS.dashboard.pageAutoHideDays,
  DEFAULT_AUTO_HIDE_DAYS,
  safeStorage,
  { getOnInit: true }
)

// Auth Storage
export const resetPasswordEmailAtom = atomWithStorage<{ email: string }>(
  LOCAL_STORAGE_KEYS.RESET_PASSWORD_EMAIL,
  { email: '' },
  safeStorage,
  {
    getOnInit: true,
  }
)

export const soundSettingsAtom = atomWithStorage<{ tradeSound: boolean }>(
  STORAGE_KEYS.settings.sound,
  { tradeSound: true },
  safeStorage,
  { getOnInit: true }
)

/**
 * Layout lock: once a trader has arranged their pages, widgets can no longer be dragged or
 * resized (WidgetShell's readOnly), so a stray drag can't wreck the layout. Adding, closing
 * and the ⋮ menu's layout actions stay available — they're deliberate, not accidents.
 */
export const layoutLockedAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.settings.layoutLocked,
  false,
  safeStorage,
  { getOnInit: true }
)

export const CHART_ENGINES = ['tradingview', 'percept'] as const
export type ChartEngine = (typeof CHART_ENGINES)[number]

/** Which library draws every chart widget: TradingView or Percept (TitanCharts). App-wide, so
 *  General settings and each chart's ⋮ → Chart Settings flip the same value. */
export const chartEngineAtom = atomWithStorage<ChartEngine>(
  STORAGE_KEYS.settings.chartEngine,
  'tradingview',
  safeStorage,
  { getOnInit: true }
)

/** Whether a chart snaps to 1s each time it enters a Papertrade tab (Chart Settings). App-wide;
 *  off keeps the tab's own timeframe, as on every other venue. */
export const papertradeOneSecondEntryAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.settings.papertradeOneSecondEntry,
  false,
  safeStorage,
  { getOnInit: true }
)

export const STREAM_INTERVAL_MODES = ['auto', 'realtime', 'saver'] as const
export type StreamIntervalMode = (typeof STREAM_INTERVAL_MODES)[number]

/** How often the server sends public market data on this device's sockets (General settings,
 *  ⌘K, the mobile menu). Defaults to real-time everywhere, phones included; `auto` (opt-in)
 *  picks data saver on the mobile layout or when the browser asks to save data. Kept here
 *  because the server forgets it per socket — see service/ws/stream-interval.ts. */
export const streamIntervalModeAtom = atomWithStorage<StreamIntervalMode>(
  STORAGE_KEYS.settings.streamInterval,
  'realtime',
  safeStorage,
  { getOnInit: true }
)

export const ARB_ANALYSIS_SECTIONS = ['spreadDistribution', 'venueFunding'] as const
export type ArbAnalysisSection = (typeof ARB_ANALYSIS_SECTIONS)[number]

/** Optional sections the user added under the arb builder's analysis panel (+ Add panel). */
export const arbAnalysisSectionsAtom = atomWithStorage<ArbAnalysisSection[]>(
  STORAGE_KEYS.settings.arbAnalysisSections,
  [],
  safeStorage,
  { getOnInit: true }
)

/** Privacy mode: account-level amounts (balances, sizes, PnL) render as MASK. Market data
 *  stays visible. Read through useNumberFormat()/<Sensitive>, never directly in a cell. */
export const hideBalancesAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.settings.hideBalances,
  false,
  safeStorage,
  { getOnInit: true }
)

export const numberFormatAtom = atomWithStorage<NumberFormatPrefs>(
  STORAGE_KEYS.settings.numberFormat,
  localeNumberFormat(),
  safeStorage,
  { getOnInit: true }
)

// Storage Reset Function.
// `userId` lets a caller that already knows which user it's clearing (e.g. logout,
// which can fire this before or after clearing the auth cookie) pin that explicitly,
// instead of depending on re-reading the cookie — which would resolve to 'guest' once
// the cookie's gone, clearing the wrong (or no) bucket.
export const resetAllStorage = (userId?: string) => {
  if (typeof window === 'undefined') return

  // Cancel (not flush) any writes still debounced in safeStorage — this function
  // removes keys directly via `localStorage.removeItem`, bypassing that adapter, so a
  // pending write left to fire later would silently resurrect the data being cleared.
  clearPendingStorageWrites()

  const resolvedUserId = userId ?? getCurrentUserId()

  // Dashboard storage — these keys are namespaced by user id under the hood (see
  // lib/safe-storage.ts); removeItem must resolve the SAME namespaced key, or this
  // is a no-op against a key nothing was ever written under.
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.exchange, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.watchList, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.config, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.layouts, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.focusedPageId, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.pageTabsOrder, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.pageLastUsedAt, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.pinnedPageIds, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.hiddenPageIds, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.pageAutoHideDays, resolvedUserId))
  localStorage.removeItem(namespacedKey(STORAGE_KEYS.dashboard.uiStateCache, resolvedUserId))

  // Per-page/per-widget storage (keys are dynamic: base-key + instance id, then the
  // storage adapter appends `:userId` at the very end). Scoped to `resolvedUserId`'s
  // own entries only — on a shared browser, other users' per-instance entries must
  // survive this user's logout/login reset.
  const linkingGroupsBase = STORAGE_KEYS.dashboard.linkingGroups
  const unlinkedCardsBase = STORAGE_KEYS.dashboard.unlinkedCards
  const riskSlidesScenariosBase = STORAGE_KEYS.riskSlides.savedScenarios
  // Also matches the exact global key removed above (harmless no-op re-remove) plus every
  // scoped variant dashboardConfigAtomFamily creates (e.g. `-risk-slides`, `-hedge`).
  const dashboardConfigBase = STORAGE_KEYS.dashboard.config
  const suffix = `:${resolvedUserId}`
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (
      key &&
      key.endsWith(suffix) &&
      (key.startsWith(linkingGroupsBase) ||
        key.startsWith(unlinkedCardsBase) ||
        key.startsWith(riskSlidesScenariosBase) ||
        key.startsWith(dashboardConfigBase))
    ) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))

  // Auth storage
  localStorage.removeItem(LOCAL_STORAGE_KEYS.RESET_PASSWORD_EMAIL)
  localStorage.removeItem(LOCAL_STORAGE_KEYS.INPUT)
}
